import { cx } from "@unizpay/ui";
import { type KeyboardEvent, type ReactNode, useEffect, useId, useRef, useState } from "react";

/** Classes for a row in a <Menu>; give it `role="menuitem"` (or `menuitemradio`) and `tabIndex={-1}`. */
export const menuItemClass =
  "flex w-full items-center gap-2 px-3 py-1.5 text-left text-[0.8125rem] outline-none hover:bg-subtle focus-visible:bg-subtle disabled:opacity-60";

function itemsOf(panel: HTMLElement | null): HTMLElement[] {
  return panel ? Array.from(panel.querySelectorAll<HTMLElement>('[role^="menuitem"]')) : [];
}

/**
 * Dropdown menu (WAI-ARIA menu button). Opening focuses the checked item or the first one; arrow
 * keys, Home and End move between items; Escape closes and returns focus to the button; Tab or a
 * click outside closes. `children` gets `close(returnFocus = true)` for the items to call.
 */
export function Menu({
  label,
  trigger,
  triggerClassName,
  header,
  align = "right",
  side = "bottom",
  panelClassName,
  children,
}: {
  /** Accessible name of the button and the menu. */
  label: string;
  trigger: ReactNode;
  triggerClassName?: string;
  /** Shown above the items, outside the menu role (e.g. who is signed in). */
  header?: ReactNode;
  align?: "left" | "right";
  /** Opens below the button, or above it (for a button at the bottom of the screen). */
  side?: "top" | "bottom";
  panelClassName?: string;
  children: (close: (returnFocus?: boolean) => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const items = itemsOf(menuRef.current);
    (items.find((el) => el.getAttribute("aria-checked") === "true") ?? items[0])?.focus();
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  function close(returnFocus = true) {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }

  function onKeyDown(e: KeyboardEvent) {
    if (!open) {
      if (e.target === buttonRef.current && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === "Tab") {
      setOpen(false);
      return;
    }
    const items = itemsOf(menuRef.current);
    const i = items.indexOf(document.activeElement as HTMLElement);
    const next = {
      ArrowDown: items[(i + 1) % items.length],
      ArrowUp: items[(i - 1 + items.length) % items.length],
      Home: items[0],
      End: items.at(-1),
    }[e.key];
    if (next) {
      e.preventDefault();
      next.focus();
    }
  }

  return (
    <div ref={rootRef} className="relative" onKeyDown={onKeyDown}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
        className={triggerClassName}
      >
        {trigger}
      </button>
      {open && (
        <div
          className={cx(
            "absolute z-30 w-64 max-w-[calc(100vw-2rem)] rounded-lg border border-line bg-surface py-1 shadow-lg",
            align === "right" ? "right-0" : "left-0",
            side === "top" ? "bottom-full mb-1" : "mt-1",
            panelClassName,
          )}
        >
          {header}
          <div ref={menuRef} id={menuId} role="menu" aria-label={label}>
            {children(close)}
          </div>
        </div>
      )}
    </div>
  );
}
